import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { ChatCircleTextIcon } from "@phosphor-icons/react/dist/csr/ChatCircleText";
import { CopyIcon } from "@phosphor-icons/react/dist/csr/Copy";
import { EnvelopeSimpleIcon } from "@phosphor-icons/react/dist/csr/EnvelopeSimple";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { toast } from "@volca/ui/lib/toast";
import { count } from "@volca/ui/trades";
import { money } from "@/profitability/review/labels";
import type { Finding } from "@/profitability/review/types";
import { formatSummaryValue } from "@/profitability/utils";
import { formatChange } from "./overview-figures";
import type { OverviewFigure, OverviewInsight } from "./types";

/**
 * The period's review as plain text an owner can paste into an email or a
 * message: the figures and their change, what stands out, and what is still
 * open by rule, with a link back.
 */
export const summaryText = ({
  title,
  period,
  figures,
  insights,
  findings,
  noun,
  plural,
  link,
  profit = true,
  currency,
}: {
  title: string;
  period: string;
  figures: OverviewFigure[];
  insights: OverviewInsight[];
  findings: Finding[];
  noun: string;
  plural: string;
  link: string;
  /** Money under the average only reads for a profit margin. */
  profit?: boolean;
  /** The report's currency; USD when it declared none. */
  currency?: string;
}) => {
  const rules = new Map<string, Finding[]>();
  for (const finding of findings) {
    const rule = finding.rule;
    rules.set(rule, [...(rules.get(rule) ?? []), finding]);
  }
  const lines = [
    `${title}, ${period}`,
    "",
    ...figures.map(
      (figure) =>
        `${figure.label}: ${formatSummaryValue(figure, currency)}${figure.change ? ` (${formatChange(figure, currency)})` : ""}`,
    ),
    ...(insights.length
      ? [
          "",
          "What stands out",
          ...insights.map((insight) => `- ${insight.text}`),
        ]
      : []),
    ...(rules.size
      ? [
          "",
          `Still open: ${count(findings.length, noun, plural)}`,
          ...[...rules.entries()].map(([rule, items]) => {
            const below = items.reduce(
              (sum, item) => sum + Math.max(item.shortfall ?? 0, 0),
              0,
            );
            return `- ${rule}: ${count(items.length, noun, plural)}${profit && below ? `, ${money(below, currency)} under average` : ""}`;
          }),
        ]
      : []),
    "",
    link,
  ];
  return lines.join("\n");
};

/**
 * Sends the week on: summarized by Volca in the chat, copied for a message,
 * or opened as an email draft.
 */
export const ShareSummary = ({
  subject,
  text,
  onSummarize,
}: {
  subject: string;
  text: () => string;
  /** Asks Volca for a summary in the chat beside the page. */
  onSummarize?: () => void;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger render={<Button variant="secondary" />}>
      Share
      <CaretDownIcon aria-hidden data-icon="inline-end" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="min-w-52">
      {onSummarize && (
        <>
          <DropdownMenuItem onClick={onSummarize}>
            <ChatCircleTextIcon aria-hidden />
            Summarize with Volca
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      <DropdownMenuItem
        onClick={() =>
          void navigator.clipboard.writeText(text()).then(
            () => toast.success("Summary copied"),
            () => toast.error("The summary could not be copied"),
          )
        }
      >
        <CopyIcon aria-hidden />
        Copy summary
      </DropdownMenuItem>
      <DropdownMenuItem
        render={
          <a
            href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text())}`}
          />
        }
      >
        <EnvelopeSimpleIcon aria-hidden />
        Email summary
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
